import { useMemo, useEffect, useImperativeHandle, forwardRef } from "react";
import { z, ZodTypeAny } from "zod";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import {
  type QuizQuestionSet as QuizQuestionSetType,
  buildFormSchemaFromFields,
  filterFieldsByQuestionLogic,
} from "@core/features/quiz";
import { PublicQuizQuestion, publicQuizQuestionsFrom } from "@platform/features/programQuiz";

export type QuizQuestionContentRef = {
  /** Trigger form submission from parent */
  submit: () => Promise<void>;
  /** Check if form is valid */
  isValid: boolean;
};

export type Props = {
  /** The current active question set to display */
  activeQuestionSet: QuizQuestionSetType;
  /** Filter function for questions in the set */
  filterQuestionSet: (
    question: QuizQuestionSetType["questions"][number],
    index: number
  ) => boolean;
  /** Handler for submitting the current question set */
  onSubmit: (data: z.infer<ZodTypeAny>) => Promise<void>;
  /** Form default values */
  formData: Record<string, unknown>;
  /** Current step index for animation key */
  stepIndex: number;
};

/**
 * Quiz question content component for FitQuizLayout.
 * Renders quiz questions without navigation controls - those are handled by the parent layout.
 * Each question is drawn by PublicQuizQuestion, as the intake forms draw theirs.
 * Exposes a ref for the parent to trigger form submission.
 */
export const QuizQuestionContent = forwardRef<QuizQuestionContentRef, Props>(
  function QuizQuestionContent(
    {
      activeQuestionSet,
      filterQuestionSet,
      onSubmit,
      formData,
      stepIndex,
    },
    ref
  ) {
    const questions = useMemo(
      () =>
        publicQuizQuestionsFrom(
          activeQuestionSet.questions.filter(filterQuestionSet)
        ),
      [activeQuestionSet, filterQuestionSet]
    );
    const allFields = useMemo(() => questions.map((question) => question.field), [questions]);

    const schema = useMemo(
      () => buildFormSchemaFromFields(allFields),
      [allFields]
    );

    // Build default values with force_yes fields set to true
    const defaultValues = useMemo(() => {
      const forceYesDefaults: Record<string, unknown> = {};
      for (const field of allFields) {
        if (field.isForceYes) {
          forceYesDefaults[field.name] = true;
        }
      }
      return { ...forceYesDefaults, ...formData };
    }, [allFields, formData]);

    const { form, handleSubmit } = useForm({
      defaultValues,
      schema,
      onSubmit,
    });

    useEffect(() => {
      form.reset(defaultValues);
    }, [defaultValues, form]);

    const values = form.watch();

    const visibleQuestions = useMemo(() => {
      const shown = new Set(filterFieldsByQuestionLogic(allFields, values).map((field) => field.name));
      return questions.filter((question) => shown.has(question.name));
    }, [allFields, questions, values]);

    const { isValid } = form.formState;

    // Expose submit method and isValid state to parent
    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      isValid,
    }), [handleSubmit, isValid]);

    return (
      <Form
        key={stepIndex}
        form={form}
        className="w-full"
        innerClassName="w-full flex flex-col gap-8"
        errorContainerClassName="mb-4"
      >
        {visibleQuestions.map((question) => (
          <PublicQuizQuestion key={question.name} question={question} className="gap-2" />
        ))}
      </Form>
    );
  }
);

export default QuizQuestionContent;
